import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import bellIcon from "../assets/bell_icon.png";
import {
  acknowledgePontosenseEvent,
  dismissAllNotifications,
  fetchPontosenseNotifications,
} from "../api/pontosenseService";
import { apiRequest } from "../api/baseApi";
import { acknowledgeVitalsAlert } from "../api/watchService";
import { acknowledgeIpawsNotification } from "../api/bellNotificationsService";
import { AuthContext } from "../state/AuthContext";
import { addBreadcrumb } from "../config/sentry";
import {
  playCriticalAlertTone,
  unlockCriticalAlertAudio,
} from "./criticalAlertSound";
import ConfirmModal from "./modals/ConfirmModal";
import "./NotificationAlert.css";

// GMA-1442: how often the bell re-checks the server for new alerts while the
// page is open. The bell used to fetch only once on mount, so a caregiver had
// to reload the browser before a new fall or offline alert appeared. Polling
// on this cadence (plus a refetch when the tab regains focus) keeps the bell
// fresh without a manual reload and without depending on web push being
// configured for the caregiver. 30s balances freshness against request volume.
export const NOTIFICATION_POLL_MS = 30000;

// GMA-1363: Alert types and severities that get red, critical treatment in the
// bell dropdown so caregivers do not scroll past them. Includes GMA-1362 device
// alerts (mic permission, etc.) plus any alert whose type or severity is
// "critical" (e.g. Pontosense via mergeAlerts).
export const CRITICAL_NOTIFICATION_TYPES = Object.freeze([
  "client_device_alert",
  "client_mic_permission_denied",
]);

export const isCriticalNotification = (alert) => {
  if (!alert) return false;
  return (
    CRITICAL_NOTIFICATION_TYPES.includes(alert.type) ||
    // GMA-1363 (Copilot #195): mergeAlerts normalizes Pontosense alerts with
    // their severity stored in `type` (e.g. type: "critical"), so a "critical"
    // type must also count as critical — not just the dedicated severity field.
    alert.type === "critical" ||
    alert.severity === "critical"
  );
};

export const criticalAlertCount = (alerts) =>
  alerts.filter(isCriticalNotification).length;

export const mergeAlerts = (legacyAlerts, pontosensePayload) => {
  // GMA-1448: the bell feed is vendor-agnostic — each notification carries its
  // own `source` (the IoT vendor name: pontosense, apple_watch, ...). Tag them
  // `isIotEvent` so any vendor's alert can be dismissed via the shared
  // iot-events acknowledge endpoint, not only Pontosense.
  const iotAlerts = (pontosensePayload?.notifications ?? []).map((n) => ({
    type: n.severity || "info",
    message: n.message,
    createdAt: n.eventAt,
    // Never defaulted to a vendor. This value is carried into the
    // notification.bell.acknowledge breadcrumb — the record of who saw a fall —
    // so inventing "pontosense" for an event the backend could not attribute
    // (it sends `event.vendor?.name ?? null`) misfiles that acknowledgement
    // under the wrong vendor on a multi-vendor platform.
    //
    // `||` rather than `??` on purpose: an empty-string vendor is not an
    // attribution either, and recording one in the audit trail is the same
    // defect as inventing a name. Every falsy value means "unattributed".
    source: n.source || null,
    isIotEvent: true,
    clientId: n.clientId,
    id: n.id,
  }));
  const combined = [...legacyAlerts, ...iotAlerts];
  combined.sort(
    (a, b) =>
      new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime(),
  );
  return combined;
};

/**
 * #756: rebuild a Pontosense-shaped payload from currently displayed IoT rows
 * so a failed IoT fetch can preserve those rows instead of wiping them.
 * Keep id-less IoT rows too — they are non-dismissible and the clear path
 * intentionally leaves them; dropping them here would make a transient IoT
 * outage erase them whenever the legacy arm succeeds.
 */
export const iotAlertsToPontosensePayload = (alerts) => ({
  notifications: (alerts || [])
    .filter((a) => a?.isIotEvent)
    .map((a) => ({
      id: a.id,
      severity: a.type,
      message: a.message,
      eventAt: a.createdAt,
      source: a.source || null,
      clientId: a.clientId,
    })),
});

// GMA-1318: a stable identity for an alert so the bell can tell a genuinely new
// critical alert from one already on screen across poll cycles. IoT-feed alerts
// carry a server id; legacy alerts (mic-denied, etc.) are keyed on their content
// + timestamp since they have no id.
export const alertKey = (alert) => {
  if (alert.isIotEvent && alert.id !== null && alert.id !== undefined) {
    return `iot-${alert.id}`;
  }
  // #574 (review): IPAWS and vitals rows carry server ids — use them, so two
  // legacy rows that happen to share a timestamp and message can never
  // collide (their ids come from different tables, so the key includes the
  // kind). Id-less legacy rows (mic-denied) keep the content key.
  if (alert.id !== null && alert.id !== undefined) {
    return `${alert.type}-${alert.id}`;
  }
  return `legacy-${alert.createdAt ?? ""}-${
    alert.message ?? alert.notificationText ?? ""
  }`;
};

/**
 * #577 review — the safe fields of a failed request, for logs and telemetry.
 *
 * An error from this component's service layer carries a message derived from
 * the RESPONSE BODY (api/responseUtils parseJsonOrThrow: body.message ||
 * body.error), so it can contain IPAWS or vitals alert text. Sentry records
 * console calls as breadcrumbs and session replay captures them, so
 * `console.error("...", err)` leaks by the same door the telemetry contract
 * closes. Log THIS instead of the error — the status is ours, not the
 * server's prose, and it is what actually distinguishes an expired session
 * from an outage.
 */
const safeErrorFields = (err) => ({
  status: err?.status ?? null,
  errorName: err?.name ?? "Error",
});

/**
 * #756: true when the bell can acknowledge this row on the server.
 *
 * Matches every kind `dismissActionFor` can clear. Used for both the
 * Clear-all button visibility and the post-clear filter so IPAWS/vitals
 * rows cannot strand the caregiver with a full bell and no Clear all
 * (the GMA-1478 gate only knew about IoT).
 */
export const isServerDismissibleAlert = (alert) => {
  if (!alert) return false;
  if (alert.isIotEvent && alert.id !== null && alert.id !== undefined) {
    return true;
  }
  if (
    alert.type === "ipaws_alert" &&
    alert.id !== null &&
    alert.id !== undefined
  ) {
    return true;
  }
  if (
    alert.type === "vitals_alert" &&
    alert.id !== null &&
    alert.id !== undefined &&
    alert.clientId !== null &&
    alert.clientId !== undefined
  ) {
    return true;
  }
  return false;
};

const NotificationAlert = ({ isDropdownVisible }) => {
  const { currentUser } = useContext(AuthContext);
  const [alerts, setAlerts] = useState([]);
  const [dismissingIds, setDismissingIds] = useState(() => new Set());
  // GMA-1478: "Clear all" is confirm-gated so an accidental click can't wipe
  // the bell.
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  // #756 / Copilot: freeze the dismissible set when the confirm opens so a
  // poll that lands while the modal is up cannot add IPAWS/vitals rows the
  // caregiver never saw into the clear. IoT still uses the bulk endpoint,
  // which cannot offer the same guarantee without server support.
  const clearAllSnapshotRef = useRef([]);
  // #756 / Copilot: invalidate in-flight bell polls when Clear all lands so a
  // stale `setAlerts(merged)` cannot restore rows we just acknowledged.
  // `runRefreshRef` lets Clear all request an authoritative post-clear fetch.
  const bellDataEpochRef = useRef(0);
  const runRefreshRef = useRef(null);
  const clearAllInFlightRef = useRef(false);
  const alertsRef = useRef([]);
  // Caregiver id that owns alertsRef — used to detect A→B switches without
  // unmount so failed-feed preservation cannot leak PHI across sessions.
  const alertsOwnerIdRef = useRef(null);
  // GMA-1318: keys of the critical alerts seen on the previous poll, so a fall
  // that newly arrives while the tab is open can sound an audible beep. null
  // until the first fetch resolves — the initial load never beeps (we don't
  // want to blast a sound for alerts already present when the dashboard opens).
  const prevCriticalKeysRef = useRef(null);

  useEffect(() => {
    alertsRef.current = alerts;
  }, [alerts]);

  // #574 / cortex-be#1114: every server-backed bell source is click-to-
  // dismiss, each through its own acknowledge endpoint (all of them record
  // acknowledged_at — the audit trail of who saw the alert). Returns null for
  // kinds with no server row to acknowledge (mic-permission items are live
  // device state and clear when the permission is re-granted).
  const dismissActionFor = (alert) => {
    if (alert.isIotEvent && alert.id !== null && alert.id !== undefined) {
      // GMA-1448: any IoT-feed alert (Pontosense, Apple Watch, Xandar, …) —
      // the acknowledge endpoint is keyed on the iot_event id, not the vendor.
      return () => acknowledgePontosenseEvent(alert.id);
    }
    if (
      alert.type === "ipaws_alert" &&
      alert.id !== null &&
      alert.id !== undefined
    ) {
      return () => acknowledgeIpawsNotification(alert.id);
    }
    if (
      alert.type === "vitals_alert" &&
      alert.id !== null &&
      alert.id !== undefined &&
      alert.clientId !== null &&
      alert.clientId !== undefined
    ) {
      // Same endpoint the Watch card uses — one acknowledged_at record
      // whichever surface the caregiver dismissed from.
      return () => acknowledgeVitalsAlert(alert.clientId, alert.id);
    }
    return null;
  };

  const dismissAlert = async (alert) => {
    const action = dismissActionFor(alert);
    const key = alertKey(alert);
    if (!action || dismissingIds.has(key)) return;
    // Breadcrumb identity: the IoT feed carries a vendor `source`; legacy
    // kinds are identified by their type. For an UNATTRIBUTED IoT row the
    // source stays null — mergeAlerts stores severity in `type` for IoT rows,
    // so falling back to it would misfile the acknowledgment under
    // "critical"/"warning" as if that were a vendor. Ids only — never PII/PHI.
    const source = alert.isIotEvent ? alert.source || null : alert.type;
    setDismissingIds((prev) => new Set(prev).add(key));
    try {
      await action();
      setAlerts((prev) => prev.filter((a) => alertKey(a) !== key));
      // #253: leave an ops trail for the acknowledge-audit boundary (the POST
      // sets acknowledged_at — the server record of who saw the alert).
      addBreadcrumb("notification.bell.acknowledge", {
        category: "notification.bell.acknowledge",
        level: "info",
        source,
        id: alert.id,
      });
    } catch (err) {
      console.error("Failed to dismiss notification:", safeErrorFields(err));
      // #253: a dropped acknowledge leaves the alert recorded as never-seen
      // with no explanation — surface the failure (no PII/PHI, ids + message).
      // #577 review: never forward err.message. parseJsonOrThrow derives it
      // from the response body, so it is arbitrary backend text and is the
      // one field on this path that can carry alert content — which would
      // contradict this component's ids-only telemetry contract. The status
      // code is ours, not the server's prose, and is what ops actually needs
      // to tell an auth expiry from an outage.
      addBreadcrumb("notification.bell.acknowledge_failed", {
        category: "notification.bell.acknowledge_failed",
        level: "error",
        source,
        id: alert.id,
        status: err?.status ?? null,
        errorName: err?.name ?? "Error",
      });
    } finally {
      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // GMA-1478 / #756: clear every server-dismissible bell row (confirm-gated
  // above). IoT uses the bulk clear-all endpoint; IPAWS and vitals use their
  // per-row acknowledge endpoints (no bulk API yet). Mic-denied / live device
  // state stays until the underlying condition clears.
  //
  // IoT acknowledge is shared across caregivers (one `acknowledged_at` on the
  // event). IPAWS acknowledge is per-caregiver ledger row. Both are intentional.
  const handleClearAll = async () => {
    setClearingAll(true);
    // Block timer/visibility refreshes for the duration, and invalidate any
    // poll that started before this clear (#756 Copilot).
    clearAllInFlightRef.current = true;
    bellDataEpochRef.current += 1;
    // Tie this clear to the caregiver who started it — if the session switches
    // mid-ack, skip all local state/refresh work for the new caregiver.
    const ownerIdAtStart = currentUser?.id ?? null;
    // Consume the open-modal snapshot — not live `alerts` (poll can add rows
    // while the confirm covers the bell).
    const snapshot = clearAllSnapshotRef.current;
    const dismissible = snapshot.filter(isServerDismissibleAlert);
    const hasIot = dismissible.some(
      (a) => a.isIotEvent && a.id !== null && a.id !== undefined,
    );
    const nonIot = dismissible.filter(
      (a) => !(a.isIotEvent && a.id !== null && a.id !== undefined),
    );
    try {
      const tasks = [];
      if (hasIot) {
        // Bulk IoT clear acknowledges every in-scope unread event for the
        // caller, including any that arrived after the modal opened — the
        // endpoint has no id list. IPAWS/vitals below are snapshot-scoped.
        tasks.push(
          dismissAllNotifications().then((result) => ({
            kind: "iot",
            cleared: result?.cleared ?? 0,
          })),
        );
      }
      for (const alert of nonIot) {
        const action = dismissActionFor(alert);
        if (!action) continue;
        const key = alertKey(alert);
        tasks.push(
          action().then(() => ({ kind: "row", key, cleared: 1 })),
        );
      }
      const settled = await Promise.allSettled(tasks);
      if (alertsOwnerIdRef.current !== ownerIdAtStart) {
        // Caregiver switched while acks were in flight — network side effects
        // for the old session may have landed, but do not rewrite B's bell.
        return;
      }
      // Invalidate again after acks settle so a poll that started mid-ack with
      // the first bumped epoch (still reading pre-ack rows) is discarded too.
      bellDataEpochRef.current += 1;
      const anyFailed = settled.some((r) => r.status === "rejected");
      const clearedKeys = new Set();
      let clearedCount = 0;
      let iotBulkOk = false;
      for (const r of settled) {
        if (r.status !== "fulfilled") continue;
        if (r.value.kind === "iot") {
          clearedCount += r.value.cleared;
          iotBulkOk = true;
          for (const a of dismissible) {
            if (a.isIotEvent && a.id !== null && a.id !== undefined) {
              clearedKeys.add(alertKey(a));
            }
          }
        } else if (r.value.kind === "row") {
          clearedCount += r.value.cleared;
          clearedKeys.add(r.value.key);
        }
      }
      // Drop only rows whose acknowledge succeeded. On a partial failure the
      // remaining dismissible rows keep Clear all available (#756). After a
      // successful IoT bulk, drop every IoT row in local state (the server
      // may have cleared more than the snapshot).
      if (clearedKeys.size > 0 || iotBulkOk) {
        setAlerts((prev) => {
          const next = prev.filter((a) => {
            if (
              iotBulkOk &&
              a.isIotEvent &&
              a.id !== null &&
              a.id !== undefined
            ) {
              return false;
            }
            return !clearedKeys.has(alertKey(a));
          });
          // Sync before the post-clear refresh — useEffect would lag a paint
          // and a failed legacy arm would re-merge the pre-clear snapshot.
          alertsRef.current = next;
          return next;
        });
      }
      if (anyFailed) {
        addBreadcrumb("notification.bell.clear_all_failed", {
          category: "notification.bell.clear_all",
          level: "error",
          cleared: clearedCount,
        });
      } else {
        addBreadcrumb("notification.bell.clear_all", {
          category: "notification.bell.clear_all",
          level: "info",
          count: clearedCount,
        });
      }
      // Authoritative re-fetch after acks settle (queued if a poll is mid-flight).
      if (typeof runRefreshRef.current === "function") {
        await runRefreshRef.current("clear_all");
      }
    } catch (err) {
      console.error("Failed to clear all notifications:", safeErrorFields(err));
      addBreadcrumb("notification.bell.clear_all_failed", {
        category: "notification.bell.clear_all",
        level: "error",
      });
    } finally {
      // Identity effect already reset UI on a caregiver switch. Do not close
      // B's modal / clear B's snapshot if A’s clear settles after the switch.
      if (alertsOwnerIdRef.current === ownerIdAtStart) {
        clearAllInFlightRef.current = false;
        setClearingAll(false);
        setShowClearAllConfirm(false);
        clearAllSnapshotRef.current = [];
      }
    }
  };

  const openClearAllConfirm = () => {
    // Snapshot only what this caregiver can currently see (owner-gated).
    clearAllSnapshotRef.current = (
      alertsOwnerIdRef.current === (currentUser?.id ?? null) ? alerts : []
    ).slice();
    setShowClearAllConfirm(true);
  };

  const cancelClearAllConfirm = () => {
    setShowClearAllConfirm(false);
    clearAllSnapshotRef.current = [];
  };

  useEffect(() => {
    const userId = currentUser?.id ?? null;
    // #756 / Copilot: failed-feed preservation reuses alertsRef. When the
    // mounted session switches caregiver A → B without unmounting, drop A's
    // rows immediately so a transient failure cannot merge client alert
    // details into B's bell.
    if (alertsOwnerIdRef.current !== userId) {
      alertsOwnerIdRef.current = userId;
      alertsRef.current = [];
      clearAllSnapshotRef.current = [];
      prevCriticalKeysRef.current = null;
      clearAllInFlightRef.current = false;
      bellDataEpochRef.current += 1;
      setAlerts([]);
      setClearingAll(false);
      setShowClearAllConfirm(false);
    }

    if (!currentUser) {
      return;
    }

    // GMA-1318: bind the one-time gesture listener that unlocks the AudioContext
    // (Chrome blocks audio until a user interacts with the page). Idempotent.
    unlockCriticalAlertAudio();

    let cancelled = false;
    let inFlight = false;
    let pendingRefreshSource = null;

    // GMA-1318: beep when a critical alert (e.g. a Pontosense fall) appears that
    // wasn't in the previous poll. Skips the very first load (prev === null) so
    // opening the dashboard with existing alerts is silent.
    const maybePlayCriticalSound = (merged) => {
      const criticalKeys = new Set(
        merged.filter(isCriticalNotification).map(alertKey),
      );
      const prev = prevCriticalKeysRef.current;
      if (prev !== null) {
        for (const key of criticalKeys) {
          if (!prev.has(key)) {
            playCriticalAlertTone();
            break;
          }
        }
      }
      prevCriticalKeysRef.current = criticalKeys;
    };

    const fetchNotifications = async () => {
      // #257: route the legacy feed through apiRequest (same v2 base URL as the
      // old raw fetch) so it inherits getValidAccessToken() + the GMA-1375
      // one-shot 401 → refresh → retry. The legacy feed carries critical
      // device alerts (client_device_alert, client_mic_permission_denied); on a
      // token-expiry race the old raw fetch got a 401 and silently emptied,
      // leaving caregivers blind to a critical alert for up to one poll cycle.
      const epochAtStart = bellDataEpochRef.current;
      const [legacyResult, pontosenseResult] = await Promise.allSettled([
        apiRequest("users/notifications", { version: "v2" }),
        fetchPontosenseNotifications(),
      ]);

      if (cancelled || epochAtStart !== bellDataEpochRef.current) return;

      let legacyAlerts = [];
      let legacyOk = false;
      if (legacyResult.status === "fulfilled" && legacyResult.value?.ok) {
        try {
          legacyAlerts = await legacyResult.value.json();
          legacyOk = true;
        } catch (_parseErr) {
          legacyOk = false;
        }
      } else if (legacyResult.status === "fulfilled") {
        console.error("Failed to fetch notifications");
      } else {
        console.error(
          "Error fetching notifications:",
          safeErrorFields(legacyResult.reason),
        );
      }

      // PR #155 review: re-check `cancelled` after the awaited json() so
      // an unmount mid-parse cannot trigger a setAlerts on an unmounted
      // component.
      if (cancelled || epochAtStart !== bellDataEpochRef.current) return;

      let pontosensePayload = { notifications: [] };
      const pontosenseOk = pontosenseResult.status === "fulfilled";
      if (pontosenseOk) {
        pontosensePayload = pontosenseResult.value;
      } else {
        console.error(
          "Error fetching Pontosense notifications:",
          safeErrorFields(pontosenseResult.reason),
        );
      }

      if (cancelled || epochAtStart !== bellDataEpochRef.current) return;

      // #756: never wipe a feed on a transient failure — preserve the previous
      // rows for any arm that did not return successfully (mic/live-state and
      // partially-failed Clear all must not vanish on the post-clear refresh).
      // Only reuse cache when it still belongs to this caregiver.
      const cacheIsOurs = alertsOwnerIdRef.current === currentUser?.id;
      if (!legacyOk && !pontosenseOk) return;
      const legacyForMerge = legacyOk
        ? legacyAlerts
        : cacheIsOurs
          ? alertsRef.current.filter((a) => !a.isIotEvent)
          : [];
      const pontosenseForMerge = pontosenseOk
        ? pontosensePayload
        : cacheIsOurs
          ? iotAlertsToPontosensePayload(alertsRef.current)
          : { notifications: [] };

      const merged = mergeAlerts(legacyForMerge, pontosenseForMerge);
      // GMA-1318: sound a beep for a newly-arrived critical alert before the
      // re-render so caregivers hear a fall even with the tab in front.
      maybePlayCriticalSound(merged);
      alertsRef.current = merged;
      setAlerts(merged);
    };

    // GMA-1442: keep the bell current without a page reload. Re-check on a
    // short timer while the tab is visible, and re-check immediately when the
    // caregiver returns to a backgrounded tab. The timer is skipped while the
    // tab is hidden so we do not make pointless requests in the background.
    //
    // Copilot review: the interval and the visibilitychange handler can both
    // fire, so guard against overlapping fetches (which could resolve out of
    // order) and against a late timer or event running during cleanup. Only
    // one refresh runs at a time; the initial load passes no source, so it
    // does not emit an auto-refresh breadcrumb.
    //
    // #756: if Clear all requests a refresh while a poll is in flight, queue
    // it — the in-flight response is discarded via bellDataEpochRef, then the
    // queued refresh loads post-ack server state. Timer/visibility starts are
    // also deferred while Clear all is in flight.
    const runRefresh = async (source) => {
      if (cancelled) return;
      if (clearAllInFlightRef.current && source !== "clear_all") {
        pendingRefreshSource = source || "queued";
        return;
      }
      if (inFlight) {
        pendingRefreshSource = source || "queued";
        return;
      }
      inFlight = true;
      if (source) {
        addBreadcrumb("notification.bell.auto_refresh", {
          category: "notification.bell.auto_refresh",
          level: "info",
          source,
        });
      }
      try {
        await fetchNotifications();
      } finally {
        inFlight = false;
        if (!cancelled && pendingRefreshSource) {
          const next = pendingRefreshSource;
          pendingRefreshSource = null;
          await runRefresh(next);
        }
      }
    };

    runRefreshRef.current = runRefresh;
    runRefresh();

    const pollId = setInterval(() => {
      if (document.visibilityState !== "hidden") {
        runRefresh("poll");
      }
    }, NOTIFICATION_POLL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runRefresh("visibilitychange");
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      runRefreshRef.current = null;
      clearInterval(pollId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [currentUser]);

  // #756 / Copilot: identity reset is effect-timed, so an A→B context update
  // can paint once with A's `alerts` before the effect clears them. Gate the
  // rendered list on the owner ref so the prior caregiver's rows never show.
  const currentUserId = currentUser?.id ?? null;
  const visibleAlerts =
    alertsOwnerIdRef.current === currentUserId ? alerts : [];

  // GMA-1363: critical-severity awareness for the bell badge + items.
  const criticalCount = criticalAlertCount(visibleAlerts);
  const hasCritical = criticalCount > 0;

  // #756: Clear all tracks every server-dismissible kind (IoT, IPAWS, vitals),
  // not only IoT — otherwise a mixed bell loses the button after IoT clears
  // and strands IPAWS/vitals with no bulk dismiss.
  const hasDismissibleAlerts = visibleAlerts.some(isServerDismissibleAlert);

  return (
    <div className="notification-alert">
      <div className="notification-icon">
        {visibleAlerts.length > 0 && (
          <span
            className={`notification-count${hasCritical ? " critical" : ""}`}
            style={hasCritical ? { backgroundColor: "#e53935", color: "#fff" } : undefined}
            data-testid="notification-count"
            data-critical={hasCritical ? "true" : "false"}
            // GMA-1363 (Copilot #172): critical state must not be color-only.
            // Announce it to assistive tech with an explicit label + live region
            // so screen-reader caregivers hear "N urgent notifications".
            role="status"
            aria-live={hasCritical ? "assertive" : "polite"}
            aria-label={
              hasCritical
                ? `${visibleAlerts.length} notifications, ${criticalCount} urgent`
                : `${visibleAlerts.length} notifications`
            }
          >
            {visibleAlerts.length}
          </span>
        )}
        <img src={bellIcon} alt="Notifications" />
      </div>
      {isDropdownVisible && visibleAlerts.length > 0 && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            {/* #574: the 2-week auto-clear must be legible in the UI — hover
                tooltip for mouse users, and the same text as an accessible,
                keyboard-reachable label (never hover-only). Copy must match
                the backend's shared 14-day policy (bellAlertPolicy). */}
            <span
              className="notification-expiry-info"
              data-testid="bell-expiry-info"
              tabIndex={0}
              title="Notifications clear automatically after 2 weeks"
              aria-label="Notifications clear automatically after 2 weeks"
            >
              Auto-clears after 2 weeks
            </span>
            {hasDismissibleAlerts && (
              <button
                type="button"
                data-testid="bell-clear-all"
                className="notification-clear-all"
                aria-label="Clear all notifications"
                title="Clear all notifications"
                onClick={openClearAllConfirm}
              >
                Clear all
              </button>
            )}
          </div>
          {visibleAlerts.map((alert, index) => {
            // #574: dismissible = there is a server acknowledge for this kind
            // (IoT events, IPAWS, vitals). Mic-permission items are live
            // device state and stay until the permission is re-granted.
            const isDismissible = dismissActionFor(alert) !== null;
            const isCritical = isCriticalNotification(alert);
            const alertText =
              alert.message || alert.notificationText || "Sensor alert";
            const key = alertKey(alert);
            return (
              <div
                key={key}
                data-testid={`notification-item-${index}`}
                data-critical={isCritical ? "true" : "false"}
                className={`notification-item ${alert.type}${
                  isDismissible ? " notification-item--dismissible" : ""
                }${isCritical ? " critical" : ""}`}
                style={
                  isCritical
                    ? { borderLeft: "4px solid #e53935", background: "#fff5f5" }
                    : undefined
                }
                role={isDismissible ? "button" : undefined}
                tabIndex={isDismissible ? 0 : undefined}
                aria-label={
                  isDismissible ? `Dismiss notification: ${alertText}` : undefined
                }
                onClick={
                  isDismissible ? () => dismissAlert(alert) : undefined
                }
                onKeyDown={
                  isDismissible
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          dismissAlert(alert);
                        }
                      }
                    : undefined
                }
              >
                <span
                  style={{
                    backgroundColor: isCritical ? "#e53935" : "#9E9E9E",
                    color: isCritical ? "#fff" : undefined,
                    padding: "4px 16px",
                    border: "2px solid #000",
                    fontSize: "0.8rem",
                  }}
                >
                  {isCritical ? "URGENT · " : ""}
                  {moment(alert.createdAt).fromNow()}
                </span>
                {isDismissible && (
                  <span className="notification-dismiss-hint">Tap to dismiss</span>
                )}
                <br />
                <span style={{ fontSize: "1rem", display: "block", marginTop: "4px" }}>
                  {alert.message || alert.notificationText}
                </span>
              </div>
            );
          })}
        </div>
      )}
      <ConfirmModal
        isOpen={showClearAllConfirm}
        isLoading={clearingAll}
        title="Clear all notifications?"
        message="This dismisses every alert you can tap to clear (sensor, weather, and vitals). Device alerts like mic permission stay until the condition is fixed. Sensor alerts clear for every caregiver who can see those clients. You can't undo this."
        confirmLabel="Clear all"
        cancelLabel="Cancel"
        onConfirm={handleClearAll}
        onCancel={cancelClearAllConfirm}
      />
    </div>
  );
};

export default NotificationAlert;
